import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Users, HeartHandshake, ShieldCheck, MapPin, Phone, Mail, ArrowRight, CheckCircle2,
  Loader2, BedDouble, Info, CalendarClock, Lock, Building2, SlidersHorizontal, RotateCcw, Globe,
  Heart, Utensils, Sparkles, Activity, Star, Leaf,
} from "lucide-react";

const IMG = {
  soin: "/img/photos/f4e9cdbba029.jpg",
  accompagnement: "/img/photos/c4252d70bac0.jpg",
  repas: "/img/photos/1f22d2b252df.jpg",
  table: "/img/photos/d1cd15d204e0.jpg",
  activite: "/img/photos/f18226ca4c5b.jpg",
  promenade: "/img/photos/0166b120f2e1.jpg",
  portrait: "/img/photos/79a1f6dbf0b0.jpg",
};
const SERVICES = [
  { icon: HeartHandshake, img: IMG.soin, k: "svc1" },
  { icon: Utensils, img: IMG.repas, k: "svc2" },
  { icon: Sparkles, img: IMG.activite, k: "svc3" },
  { icon: Activity, img: IMG.promenade, k: "svc4" },
  { icon: ShieldCheck, img: IMG.accompagnement, k: "svc5" },
  { icon: Heart, img: IMG.portrait, k: "svc6" },
];

const AVAIL_STYLE = {
  quelques: { color: "#2dd4bf", bg: "rgba(20,184,166,0.16)", dot: "#22c55e" },
  limitee: { color: "#5eead4", bg: "rgba(13,148,136,0.16)", dot: "#f59e0b" },
  attente: { color: "#99f6e4", bg: "rgba(255,255,255,0.08)", dot: "#f97316" },
  contact: { color: "#99f6e4", bg: "rgba(255,255,255,0.08)", dot: "#94a3b8" },
};

const URGENCY = ["immediat", "semaine", "mois", "renseignement", "futur"];
const NEEDS = ["long_sejour", "court_sejour", "convalescence", "unite_protegee"];
const SITUATIONS = ["domicile", "hopital", "revalidation", "autre"];
const RELATIONS = ["fils", "fille", "conjoint", "tuteur", "assistant_social", "autre"];
const PARTNER_TYPES = ["cpas", "hopital", "assistant_social", "medecin", "service_domicile", "autre"];

const EMPTY = {
  name: "", first_name: "", last_name: "", phone: "", email: "", commune: "", urgency: "", need_type: "", message: "",
  relation: "", current_situation: "", institution: "", partner_type: "", function: "",
  wants_account: false, consent: false,
};

export default function Decouvrir() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const forced = ["famille", "partenaire"].includes(params.get("type")) ? params.get("type") : null;
  const [data, setData] = useState({ group_name: "", updated_at: "", residences: [] });
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [audience, setAudience] = useState(forced || "famille");
  const [residenceId, setResidenceId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [fCommune, setFCommune] = useState("all");
  const [fRegion, setFRegion] = useState("all");
  const [fRoom, setFRoom] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/residences");
        setData(r.data || { residences: [] });
      } catch (e) { /* silencieux : vitrine publique */ }
      setLoading(false);
    })();
  }, []);

  const lang = i18n.language || "fr";
  const locale = lang.startsWith("nl") ? "nl-BE" : lang.startsWith("en") ? "en-GB" : "fr-BE";
  const updated = useMemo(() => {
    if (!data.updated_at) return "";
    try { return new Date(data.updated_at).toLocaleString(locale, { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  }, [data.updated_at, locale]);

  const communes = useMemo(() => [...new Set((data.residences || []).map((r) => r.commune).filter(Boolean))].sort(), [data.residences]);
  const filtered = useMemo(() => (data.residences || []).filter((r) => {
    if (fCommune !== "all" && r.commune !== fCommune) return false;
    if (fRegion !== "all" && r.region !== fRegion) return false;
    if (fRoom !== "all" && !(r.room_types || []).includes(fRoom)) return false;
    return true;
  }), [data.residences, fCommune, fRegion, fRoom]);
  const hasFilters = fCommune !== "all" || fRegion !== "all" || fRoom !== "all";
  const resetFilters = () => { setFCommune("all"); setFRegion("all"); setFRoom("all"); };

  const openForm = (aud, resId) => {
    setAudience(aud); setResidenceId(resId || ""); setForm(EMPTY); setDone(false); setDlg(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!residenceId) { toast.error(t("public.selectResidence")); return; }
    if (audience === "partenaire") {
      if (!form.institution.trim() || !form.name.trim() || !form.phone.trim()) { toast.error(t("public.required")); return; }
    } else {
      if (!form.last_name.trim() || !form.phone.trim()) { toast.error(t("public.required")); return; }
    }
    if (!form.consent) { toast.error(t("public.consentRequired")); return; }
    setSending(true);
    try {
      const lg = lang.startsWith("nl") ? "nl" : lang.startsWith("en") ? "en" : "fr";
      await api.post("/public/requests", {
        requester_type: audience, residence_id: residenceId, lang: lg, ...form,
      });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || t("public.errorSend")); }
    setSending(false);
  };

  const availLabel = (k) => t(`public.avail.${k}`, { defaultValue: k });

  return (
    <div className="min-h-screen relative overflow-hidden text-white" style={{ background: "#140c14" }} data-testid="public-page">
      {/* Fond photo (personne âgée + soignant) */}
      <div className="fixed inset-0" style={{ zIndex: 0 }} aria-hidden="true">
        <img src="/novadia-hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="login-hero-overlay" />
      </div>
      {/* Ambiance */}
      <div className="login-scene" aria-hidden="true" style={{ zIndex: 1 }}>
        <div className="login-aurora" style={{ width: 560, height: 560, top: "-14%", left: "-8%", background: "radial-gradient(circle, rgba(13,148,136,0.55), transparent 62%)" }} />
        <div className="login-aurora" style={{ width: 480, height: 480, bottom: "2%", right: "-10%", background: "radial-gradient(circle, rgba(20,184,166,0.4), transparent 62%)", animationDelay: "-7s" }} />
      </div>

      {/* Top bar */}
      <header className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/carepilot-64.png" alt="CarePilot" className="h-11 w-11 rounded-2xl shrink-0" style={{ boxShadow: "0 0 32px -8px rgba(13,148,136,0.8)" }} />
          <div className="leading-tight min-w-0">
            <span className="font-display text-xl font-extrabold tracking-tight">CarePilot</span>
            {t("public.brandSub") && <div className="text-[10px] font-semibold tracking-[0.2em] uppercase" style={{ color: "#5eead4" }}>{t("public.brandSub")}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <LanguageSwitcher variant="dark" />
          <Link to="/login" data-testid="public-to-login" className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold border border-white/15 hover:border-teal-400/60 transition" title={t("public.backToLogin")}>
            <Lock className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t("public.backToLogin")}</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-6 pb-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium mb-6 backdrop-blur"
          style={{ background: "rgba(13,148,136,0.18)", borderColor: "rgba(94,234,212,0.35)", color: "#ccfbf1" }}>
          <ShieldCheck className="h-4 w-4" style={{ color: "#5eead4" }} /> {t("public.heroTag")}
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.05 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight max-w-3xl login-shimmer" data-testid="public-hero-title">
          {t("public.heroTitle")}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.15 }}
          className="mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">
          {t("public.heroSubtitle")}
        </motion.p>

        {/* Audience buttons */}
        <div className={`mt-7 grid gap-3 max-w-2xl ${forced ? "sm:grid-cols-1" : "sm:grid-cols-2"}`}>
          {(!forced || forced === "famille") && (
          <button onClick={() => openForm("famille", "")} data-testid="public-cta-family"
            className="group rounded-2xl border p-5 text-left backdrop-blur transition hover:-translate-y-0.5"
            style={{ background: "rgba(255,255,255,0.07)", borderColor: "rgba(255,255,255,0.14)" }}>
            <div className="flex items-center gap-2 font-display font-bold text-lg"><Users className="h-5 w-5" style={{ color: "#2dd4bf" }} /> {t("public.iAmFamily")}</div>
            <p className="text-sm text-white/65 mt-1.5 flex items-center gap-1">{t("public.ctaInfo")} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition" /></p>
          </button>
          )}
          {(!forced || forced === "partenaire") && (
          <button onClick={() => openForm("partenaire", "")} data-testid="public-cta-partner"
            className="group rounded-2xl border p-5 text-left backdrop-blur transition hover:-translate-y-0.5"
            style={{ background: "rgba(255,255,255,0.07)", borderColor: "rgba(255,255,255,0.14)" }}>
            <div className="flex items-center gap-2 font-display font-bold text-lg"><HeartHandshake className="h-5 w-5" style={{ color: "#2dd4bf" }} /> {t("public.iAmPartner")}</div>
            <p className="text-sm text-white/65 mt-1.5 flex items-center gap-1">{t("public.ctaInfo")} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition" /></p>
          </button>
          )}
        </div>

        {/* Disclaimer obligatoire */}
        <div className="mt-7 rounded-xl border p-4 flex items-start gap-3 max-w-3xl" data-testid="public-disclaimer"
          style={{ background: "rgba(13,148,136,0.1)", borderColor: "rgba(94,234,212,0.28)" }}>
          <Info className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#5eead4" }} />
          <p className="text-sm text-white/80 leading-relaxed">{t("public.disclaimer")}</p>
        </div>
      </section>

      {/* ═══ Section commerciale : prendre soin au quotidien ═══ */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-6" data-testid="public-showcase">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold mb-4" style={{ background: "rgba(13,148,136,0.2)", color: "#ccfbf1" }}>
              <Leaf className="h-3.5 w-3.5" style={{ color: "#5eead4" }} /> {t("public.mkt.badge")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight">
              {t("public.mkt.title")}
            </h2>
            <p className="mt-4 text-white/75 leading-relaxed">{t("public.mkt.p1")}</p>
            <p className="mt-3 text-white/75 leading-relaxed">{t("public.mkt.p2")}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => openForm("famille", "")} data-testid="showcase-cta-family"
                className="h-12 px-6 text-base font-semibold border-0 text-white rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                style={{ background: "linear-gradient(135deg, #0f766e, #0d9488 60%, #14b8a6)" }}>
                {t("public.mkt.ctaRecontact")} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <a href="#public-residences" className="inline-flex items-center gap-2 h-12 px-5 rounded-xl border border-white/20 hover:border-teal-400/60 text-sm font-semibold transition">
                <Building2 className="h-4 w-4" /> {t("public.mkt.seeResidences")}
              </a>
            </div>
          </motion.div>

          {/* Mosaïque photos */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { src: IMG.soin, cls: "rounded-3xl", h: "h-56" },
              { src: IMG.repas, cls: "rounded-full", h: "h-56 mt-8" },
              { src: IMG.activite, cls: "rounded-full", h: "h-56" },
              { src: IMG.promenade, cls: "rounded-3xl", h: "h-56 -mt-8" },
            ].map((im, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`overflow-hidden ${im.cls} ${im.h} border-2`} style={{ borderColor: "rgba(94,234,212,0.25)" }}>
                <img src={im.src} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Nos engagements (services) ═══ */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="public-services">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("public.mkt.engagementsTitle")}</h2>
          <p className="mt-3 text-white/70">{t("public.mkt.engagementsSub")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="rounded-3xl overflow-hidden border backdrop-blur group" style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.12)" }}>
                <div className="h-44 overflow-hidden">
                  <img src={s.img} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2.5">
                    <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(13,148,136,0.2)" }}>
                      <Icon className="h-5 w-5" style={{ color: "#5eead4" }} />
                    </span>
                    <h3 className="font-display font-bold text-lg leading-tight">{t(`public.mkt.${s.k}t`)}</h3>
                  </div>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">{t(`public.mkt.${s.k}d`)}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ═══ Bande CTA contact ═══ */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-14" data-testid="public-cta-band">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0f766e, #0d9488 55%, #115e59)" }}>
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-5 w-5 fill-current" style={{ color: "#fbbf24" }} />)}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white">{t("public.mkt.bandTitle")}</h2>
            <p className="mt-3 text-white/85 max-w-xl mx-auto">{t("public.mkt.bandSub")}</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Button onClick={() => openForm("famille", "")} data-testid="cta-band-family"
                className="h-12 px-7 text-base font-bold rounded-xl bg-white text-teal-800 hover:bg-teal-50 border-0 shadow-lg">
                {t("public.mkt.bandCta")} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <a href="#public-residences" className="inline-flex items-center gap-2 h-12 px-6 rounded-xl border border-white/40 text-white text-sm font-semibold hover:bg-white/10 transition">
                <BedDouble className="h-4 w-4" /> {t("public.mkt.bandRooms")}
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Résidences */}
      <section id="public-residences" className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-16">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-5">
          <h2 className="font-display text-2xl font-extrabold">{t("public.residencesTitle")}</h2>
          {updated && <p className="text-xs text-white/50">{t("public.updatedAt", { date: updated })}</p>}
        </div>

        {/* Filtres portail groupe */}
        {!loading && data.residences.length > 0 && (
          <div className="rounded-2xl border p-4 mb-5 backdrop-blur" data-testid="public-filters" style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.12)" }}>
            <div className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-3"><SlidersHorizontal className="h-4 w-4" style={{ color: "#2dd4bf" }} /> {t("public.filters.title")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-white/55">{t("public.filters.commune")}</Label>
                <Select value={fCommune} onValueChange={setFCommune}>
                  <SelectTrigger data-testid="public-filter-commune" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("public.filters.all")}</SelectItem>
                    {communes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/55">{t("public.filters.region")}</Label>
                <Select value={fRegion} onValueChange={setFRegion}>
                  <SelectTrigger data-testid="public-filter-region" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("public.filters.all")}</SelectItem>
                    {["flandre", "wallonie", "bruxelles"].map((k) => <SelectItem key={k} value={k}>{t(`public.region.${k}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/55">{t("public.filters.roomType")}</Label>
                <Select value={fRoom} onValueChange={setFRoom}>
                  <SelectTrigger data-testid="public-filter-roomtype" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("public.filters.all")}</SelectItem>
                    {["simple", "double", "unite_protegee"].map((k) => <SelectItem key={k} value={k}>{t(`public.roomType.${k}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-white/55" data-testid="public-filter-count">{t("public.filters.count", { n: filtered.length })}</span>
              {hasFilters && <button onClick={resetFilters} data-testid="public-filter-reset" className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-300 hover:text-teal-200 transition"><RotateCcw className="h-3.5 w-3.5" /> {t("public.filters.reset")}</button>}
            </div>
          </div>
        )}

        {loading && <div className="py-16 text-center text-white/60"><Loader2 className="h-7 w-7 animate-spin mx-auto" /></div>}

        {!loading && data.residences.length === 0 && (
          <div className="rounded-2xl border border-white/10 p-10 text-center text-white/70" data-testid="public-no-residences">
            <BedDouble className="h-10 w-10 mx-auto mb-3 opacity-60" />
            {t("public.noResidences")}
          </div>
        )}

        {!loading && data.residences.length > 0 && filtered.length === 0 && (
          <div className="rounded-2xl border border-white/10 p-10 text-center text-white/70" data-testid="public-no-match">
            <SlidersHorizontal className="h-10 w-10 mx-auto mb-3 opacity-60" />
            {t("public.filters.none")}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r, idx) => {
            const st = AVAIL_STYLE[r.availability] || AVAIL_STYLE.contact;
            const uniqTypes = [...new Set((r.room_types || []).map((k) => t(`public.roomType.${k}`, { defaultValue: k })))];
            return (
              <motion.div key={r.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(idx * 0.05, 0.4) }}
                className="rounded-2xl border p-5 flex flex-col backdrop-blur" data-testid="public-residence-card"
                style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.12)" }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-bold truncate flex items-center gap-2"><Building2 className="h-4 w-4 shrink-0" style={{ color: "#2dd4bf" }} /> {r.name}</h3>
                    {(r.commune || r.region) && <p className="text-sm text-white/55 mt-0.5 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{[r.commune, r.region ? t(`public.region.${r.region}`, { defaultValue: "" }) : ""].filter(Boolean).join(" · ")}</span></p>}
                  </div>
                </div>

                <div className="mt-3 inline-flex items-center gap-2 self-start rounded-full px-3 py-1.5 text-sm font-semibold" style={{ background: st.bg, color: st.color }}>
                  <span className="h-2 w-2 rounded-full" style={{ background: st.dot }} /> {availLabel(r.availability)}
                </div>

                {r.description && <p className="text-sm text-white/65 mt-3 leading-snug">{r.description}</p>}

                {uniqTypes.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5">{t("public.typesAvail")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {uniqTypes.map((tp) => (
                        <span key={tp} className="text-xs rounded-lg px-2 py-1 border border-white/12 bg-white/5 text-white/75">{tp}</span>
                      ))}
                    </div>
                    <p className="text-[11px] text-white/40 mt-1.5 italic">{t("public.confirmSubject")}</p>
                  </div>
                )}

                {(r.care_types || []).length > 0 && (
                  <div className="mt-3">
                    <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5">{t("public.careTypesLabel")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(r.care_types || []).map((k) => (
                        <span key={k} className="text-xs rounded-lg px-2 py-1 border font-medium" style={{ borderColor: "rgba(94,234,212,0.35)", background: "rgba(13,148,136,0.12)", color: "#5eead4" }}>{t(`public.care.${k}`, { defaultValue: k })}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-auto pt-4 flex flex-col gap-2">
                  {r.website && (
                    <a href={/^https?:\/\//.test(r.website) ? r.website : `https://${r.website}`} target="_blank" rel="noopener noreferrer" data-testid={`public-website-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-sm text-teal-300 hover:text-teal-200 transition truncate"><Globe className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{r.website.replace(/^https?:\/\//, "")}</span></a>
                  )}
                  <Button onClick={() => openForm(forced || "famille", r.id)} data-testid={`public-room-info-${r.id}`}
                    className="w-full h-10 border-0 font-semibold" style={{ background: "linear-gradient(135deg, #0f766e, #0d9488 55%, #2dd4bf)" }}>
                    {t("public.ctaInfo")} <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                  <div className="flex gap-2">
                    {r.contact_phone && (
                      <a href={`tel:${r.contact_phone.replace(/\s/g, "")}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl h-9 text-sm border border-white/15 hover:border-teal-400/60 transition"><Phone className="h-3.5 w-3.5" /> {r.contact_phone}</a>
                    )}
                    {r.contact_email && (
                      <a href={`mailto:${r.contact_email}`} className="inline-flex items-center justify-center gap-1.5 rounded-xl h-9 px-3 text-sm border border-white/15 hover:border-teal-400/60 transition"><Mail className="h-3.5 w-3.5" /></a>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="text-xs text-white/45 text-center mt-8">{t("public.updatedDaily")}</p>
      </section>

      <footer className="relative z-10 text-center text-xs text-white/35 pb-6">© 2026 Xavier PEYOU · Groupe Novadia</footer>

      {/* Form dialog */}
      <Dialog open={dlg} onOpenChange={(o) => { if (!o) setDlg(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="public-form-dialog">
          {done ? (
            <div className="text-center py-6" data-testid="public-form-success">
              <div className="h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8 text-emerald-400" /></div>
              <h3 className="font-display text-xl font-bold mt-4">{t("public.successTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">{t("public.successText")}</p>
              <Button onClick={() => setDlg(false)} className="mt-6" data-testid="public-form-close">{t("public.close")}</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{audience === "partenaire" ? t("public.partnerFormTitle") : t("public.familyFormTitle")}</DialogTitle>
              </DialogHeader>

              {/* Audience toggle (masqué si un type est imposé depuis la page de connexion) */}
              {!forced && (
              <div className="grid grid-cols-2 gap-2">
                {["famille", "partenaire"].map((a) => (
                  <button key={a} onClick={() => setAudience(a)} data-testid={`public-toggle-${a}`}
                    className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${audience === a ? "border-teal-400 bg-teal-400/10 text-teal-300" : "border-white/12 text-white/60"}`}>
                    {a === "famille" ? t("public.iAmFamily") : t("public.iAmPartner")}
                  </button>
                ))}
              </div>
              )}

              <div className="space-y-3 mt-1">
                {/* Résidence */}
                <div className="space-y-1.5">
                  <Label>{t("public.residenceLabel")}</Label>
                  <Select value={residenceId} onValueChange={setResidenceId}>
                    <SelectTrigger data-testid="public-form-residence"><SelectValue placeholder={t("public.residenceLabel")} /></SelectTrigger>
                    <SelectContent>
                      {data.residences.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.commune ? ` · ${r.commune}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {audience === "partenaire" && (
                  <>
                    <FieldInput label={t("public.f.institution")} value={form.institution} onChange={(v) => set("institution", v)} testid="public-f-institution" />
                    <FieldSelect label={t("public.f.partnerType")} value={form.partner_type} onChange={(v) => set("partner_type", v)} options={PARTNER_TYPES} tkey="public.partnerTypeOpt" testid="public-f-partnertype" />
                  </>
                )}

                {audience === "partenaire" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <FieldInput label={t("public.f.contactName")} value={form.name} onChange={(v) => set("name", v)} testid="public-f-name" />
                    <FieldInput label={t("public.f.function")} value={form.function} onChange={(v) => set("function", v)} testid="public-f-function" />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <FieldInput label={t("public.f.lastName")} value={form.last_name} onChange={(v) => set("last_name", v)} testid="public-f-lastname" />
                      <FieldInput label={t("public.f.firstName")} value={form.first_name} onChange={(v) => set("first_name", v)} testid="public-f-firstname" />
                    </div>
                    <FieldSelect label={t("public.f.relation")} value={form.relation} onChange={(v) => set("relation", v)} options={RELATIONS} tkey="public.relationOpt" testid="public-f-relation" />
                  </>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <FieldInput label={t("public.f.phone")} value={form.phone} onChange={(v) => set("phone", v)} testid="public-f-phone" />
                  <FieldInput label={audience === "partenaire" ? t("public.f.proEmail") : t("public.f.email")} value={form.email} onChange={(v) => set("email", v)} testid="public-f-email" />
                </div>

                <FieldSelect label={t("public.f.urgency")} value={form.urgency} onChange={(v) => set("urgency", v)} options={URGENCY} tkey="public.urgencyOpt" testid="public-f-urgency" />

                <div className="grid grid-cols-2 gap-2">
                  <FieldSelect label={t("public.f.needType")} value={form.need_type} onChange={(v) => set("need_type", v)} options={NEEDS} tkey="public.care" testid="public-f-need" />
                  {audience === "famille" &&
                    <FieldSelect label={t("public.f.situation")} value={form.current_situation} onChange={(v) => set("current_situation", v)} options={SITUATIONS} tkey="public.situationOpt" testid="public-f-situation" />}
                </div>

                <div className="space-y-1.5">
                  <Label>{t("public.f.message")}</Label>
                  <Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="public-f-message" />
                </div>

                {audience === "partenaire" && (
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.wants_account} onCheckedChange={(v) => set("wants_account", !!v)} data-testid="public-f-account" className="mt-0.5" />
                    <span className="text-white/75">{t("public.wantsAccount")}</span>
                  </label>
                )}

                {/* Confidentialité */}
                <p className="text-[11px] text-muted-foreground leading-relaxed rounded-lg bg-white/[0.03] border border-white/8 p-2.5" data-testid="public-privacy">{t("public.privacy")}</p>

                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="public-f-consent" className="mt-0.5" />
                  <span className="text-white/85 font-medium">{t("public.consent")}</span>
                </label>

                <Button onClick={submit} disabled={sending} data-testid="public-form-submit"
                  className="w-full h-11 border-0 font-semibold" style={{ background: "linear-gradient(135deg, #0f766e, #0d9488 55%, #2dd4bf)" }}>
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CalendarClock className="mr-1.5 h-4 w-4" /> {t("public.submit")}</>}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldInput({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, tkey, testid }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{t(`${tkey}.${o}`, { defaultValue: o })}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
